import React from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Expand, MonitorUp } from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { SolutionReveal } from "./SolutionMotion";
import { solutionProductEvidenceFor } from "./solutionProductEvidence";

const EASE_OUT = [0.22, 1, 0.36, 1];

function StoryPictureSource({ image, className = "" }) {
  const avif = image.variants.map((variant) => `${variant.avif} ${variant.width}w`).join(", ");
  const webp = image.variants.map((variant) => `${variant.webp} ${variant.width}w`).join(", ");
  const fallback = image.variants.find((variant) => variant.width >= 960) || image.variants.at(-1);
  return (
    <picture className={`solution-product-viewer__picture ${className}`.trim()}>
      <source type="image/avif" srcSet={avif} sizes="(max-width: 760px) 96vw, (max-width: 1180px) 88vw, 1040px" />
      <source type="image/webp" srcSet={webp} sizes="(max-width: 760px) 96vw, (max-width: 1180px) 88vw, 1040px" />
      <img
        src={fallback.webp}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        decoding="async"
        draggable="false"
        data-product-story-id={image.id}
      />
    </picture>
  );
}

function ProductStoryPicture({ image, mobileImage }) {
  return (
    <>
      <StoryPictureSource image={image} className={mobileImage ? "solution-product-viewer__picture--desktop" : ""} />
      {mobileImage ? <StoryPictureSource image={mobileImage} className="solution-product-viewer__picture--mobile" /> : null}
    </>
  );
}

export default function SolutionProductShowcase({ segment }) {
  const evidence = solutionProductEvidenceFor(segment);
  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const tabsRef = React.useRef([]);

  React.useEffect(() => setActiveIndex(0), [segment]);
  if (!evidence) return null;

  const activePanel = evidence.panels[activeIndex];
  const fullImage = activePanel.image.variants.at(-1).webp;
  const selectPanel = (index, { focus = false } = {}) => {
    setActiveIndex(index);
    if (focus) tabsRef.current[index]?.focus();
  };
  const onTabKeyDown = (event, index) => {
    const last = evidence.panels.length - 1;
    let next = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = index === last ? 0 : index + 1;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = index === 0 ? last : index - 1;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    selectPanel(next, { focus: true });
  };

  return (
    <section
      className="solution-product-stage"
      id="solution-evidence"
      tabIndex="-1"
      aria-labelledby="solution-evidence-title"
      data-testid="solution-product-evidence"
    >
      <div className="solution-product-stage__inner">
        <SolutionReveal className="solution-product-stage__heading" amount={0.2}>
          <div>
            <p><MonitorUp aria-hidden="true" /> {evidence.eyebrow}</p>
            <h2 id="solution-evidence-title"><BrandCopy>{evidence.title}</BrandCopy></h2>
          </div>
          <div>
            <p><BrandCopy>{evidence.description}</BrandCopy></p>
            <ul aria-label="Product-view qualities">
              {evidence.qualities.map((quality) => (
                <li key={quality}><Check aria-hidden="true" /> {quality}</li>
              ))}
            </ul>
          </div>
        </SolutionReveal>

        <SolutionReveal className="solution-product-browser" variant="scale" amount={0.12}>
          <div className="solution-product-browser__chrome" aria-hidden="true">
            <span /><span /><span />
            <b>Vidyapeeth360 workspace</b>
            <small>Product walkthrough</small>
          </div>
          <div className="solution-product-browser__body">
            <div className="solution-product-tabs" role="tablist" aria-label={`${evidence.eyebrow} workflow views`}>
              {evidence.panels.map((item, index) => {
                const selected = activeIndex === index;
                return (
                  <button
                    type="button"
                    role="tab"
                    id={`solution-product-tab-${segment}-${index}`}
                    aria-controls={`solution-product-panel-${segment}`}
                    aria-selected={selected}
                    tabIndex={selected ? 0 : -1}
                    key={item.label}
                    ref={(node) => { tabsRef.current[index] = node; }}
                    onClick={() => selectPanel(index)}
                    onKeyDown={(event) => onTabKeyDown(event, index)}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <b>{item.label}</b>
                    <small>{item.title}</small>
                  </button>
                );
              })}
            </div>

            <m.figure
              className="solution-product-viewer"
              id={`solution-product-panel-${segment}`}
              role="tabpanel"
              aria-labelledby={`solution-product-tab-${segment}-${activeIndex}`}
              tabIndex="0"
              key={`${segment}-${activePanel.image.id}`}
              initial={reduceMotion ? false : { opacity: 0, transform: "translateY(7px) scale(0.995)" }}
              animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
              transition={{ duration: 0.24, ease: EASE_OUT }}
            >
              <a href={fullImage} target="_blank" rel="noreferrer" aria-label={`Open full-size ${activePanel.label} product view`}>
                <ProductStoryPicture image={activePanel.image} mobileImage={activePanel.mobileImage} />
                <span className="solution-product-viewer__expand"><Expand aria-hidden="true" /> Full size</span>
              </a>
              <figcaption>
                <div>
                  <span>{activePanel.label}</span>
                  <h3>{activePanel.title}</h3>
                  <p>{activePanel.description}</p>
                </div>
                <Link to={activePanel.to}>Explore this workflow <ArrowRight aria-hidden="true" /></Link>
              </figcaption>
            </m.figure>
          </div>
        </SolutionReveal>
      </div>
    </section>
  );
}

export { ProductStoryPicture };
